import { GeoComplyErrorCodes } from '@apptypes/GeoComply.ts';
import RailsApiResponse from '@apptypes/api/RailsApiResponse.ts';
import GeoComplyLicense from '@apptypes/api/geocomply/license.ts';
import GeoComplyValidate from '@apptypes/api/geocomply/validate.ts';
import { useIsAuthenticatedFwmiMobile } from '@hooks/use-authenticated-fwmi-mobile.ts';
import { useAuth } from '@hooks/useAuth.tsx';
import { useI18n } from '@hooks/useI18n.tsx';
import { getApi, postApi } from '@utils/apiUtils.ts';
import Lockr from 'lockr';
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
  useCallback,
} from 'react';

export const GEO_KEY = (playerId?: string) => `geo-valid-until-${playerId}`;

// GeoComply tells us when to re-check via `GeolocateIn` (geolocate_in minus the
// buffer time, ~1165s in practice). These bound that value so a missing, zero or
// nonsensical interval can never leave a session with no future check at all.
const GEO_REVALIDATION_FALLBACK_MS = 15 * 60 * 1000;
// Used to re-arm after an attempt that produced no `geolocation` event, so the
// periodic chain heals itself instead of dying for the rest of the session.
const GEO_REVALIDATION_RETRY_MS = 2 * 60 * 1000;

type GeoComplyContextValue = {
  isSdkLoaded: boolean;
  isConnected: boolean;
  errorMessages: string[];
  banner: boolean | null;
  error: string | null;
  retryGeolocation: (ipChange?: boolean) => void;
  retryReconnect: () => void;
  retryCount: number;
  onCancelModal: () => void;
  errorCode: number | null;
  showModal: boolean | null;
  setShowModal: (v: boolean | null) => void;
  validating: boolean | null;
  inProgress: boolean | null;
  isGeoValid: boolean | null;
  isLnaHint: boolean | null;
  lnaHint: { description: string; image: string; title: string };
};

const GeoComplyContext = createContext<GeoComplyContextValue | undefined>(
  undefined,
);

type GeoComplyProviderProps = {
  children: ReactNode;
};

const installerKey = window.__config__.geoComplyKey;

const environment =
  process.env.TARGET_ENV === 'production' ? 'production' : 'staging';

export const GeoComplyProvider: React.FC<GeoComplyProviderProps> = ({
  children,
}) => {
  const { user, signout } = useAuth();
  const isGeoComplyDisabled = useIsAuthenticatedFwmiMobile(user.logged_in);

  const { t } = useI18n();
  const [isSdkLoaded, setIsSdkLoaded] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const [isLnaHint, setIsLnaHint] = useState(false);
  const [lnaHint, setLnaHint] = useState<{
    description: string;
    image: string;
    title: string;
  }>({ description: '', image: '', title: '' });

  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<number | null>(null);
  const [showModal, setShowModal] = useState<boolean | null>(false);
  const [validating, setValidating] = useState<boolean | null>(false);
  const [inProgress, setInProgress] = useState<boolean | null>(false);
  const [isGeoValid, setIsGeoValid] = useState<boolean | null>(null);

  const [banner, setBanner] = useState<boolean | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);

  const licenseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const licenseUpdatePromiseRef = useRef<Promise<void> | null>(null);
  const geoTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleGeoRevalidationRef = useRef<((ms: number) => void) | null>(
    null,
  );
  const hasInitializedRef = useRef(false); // 👈 run only once per app session

  const hasInitRef = useRef(false);
  const hasInitRefApi = useRef(false);

  // Clears every piece of user-visible geolocation state. The provider is never
  // unmounted across a logout/login (both are SPA transitions), so anything left
  // behind here belongs to the previous player. `errorCode` in particular drives
  // the alert banner on its own, so a stale value made the previous session's
  // failure flash up for the next player until their own check answered
  // (DEV-2423). `isLnaHint` is just as sticky: `initGeoComply` captures it and
  // bails out of `onConnect`, so a leftover hint suppressed the new player's
  // check entirely.
  const resetGeoState = useCallback(() => {
    setError(null);
    setErrorCode(null);
    setErrorMessages([]);
    setShowModal(false);
    setRetryCount(0);
    setValidating(false);
    setInProgress(false);
    setBanner(null);
    setIsGeoValid(null);
    setIsLnaHint(false);
    setLnaHint({ description: '', image: '', title: '' });
  }, []);

  const showMessage = (msg: string) => {
    setErrorMessages(prev => [...prev, msg]);
  };

  const showError = (msg: string) => {
    setError(msg);
    setBanner(true);
  };

  const scheduleLicenseUpdate = (ms: number) => {
    if (licenseTimeoutRef.current) {
      clearTimeout(licenseTimeoutRef.current);
    }
    licenseTimeoutRef.current = setTimeout(() => {
      licenseTimeoutRef.current = null;
      void updateLicense();
    }, ms);
  };

  // The license has its own lifetime (ExpiresAtUtc) and refreshes on its own
  // schedule; geolocation requests must not clear or refetch it. Blanking it via
  // setLicense('') before the replacement had been fetched meant any failure here
  // left the SDK with no license at all (DEV-2860).
  //
  // `clearOnLoad` is only true for the initial load: clear_geocomply has server
  // side effects and must not fire on the periodic refreshes (DEV-2346).
  const updateLicense = async (clearOnLoad = false) => {
    if (licenseUpdatePromiseRef.current) {
      return licenseUpdatePromiseRef.current;
    }

    const updatePromise = (async () => {
      if (clearOnLoad) {
        await clearGeolocation();
      }
      const res = await getApi<RailsApiResponse<GeoComplyLicense>>(
        '/restapi/v1/geocomply/license',
      );
      const expiresAtMs = Date.parse(res.Data.ExpiresAtUtc);
      const remainingMs = expiresAtMs - Date.now();

      window.GeoComply?.Client?.setLicense(res.Data.License);

      if (Number.isFinite(remainingMs) && remainingMs > 0) {
        const refreshBufferMs = Math.min(60_000, remainingMs * 0.1);
        scheduleLicenseUpdate(remainingMs - refreshBufferMs);
      } else {
        console.warn('- license expiry is invalid or already expired');
      }
      console.log('- license updated');
    })();

    licenseUpdatePromiseRef.current = updatePromise;
    try {
      await updatePromise;
    } finally {
      if (licenseUpdatePromiseRef.current === updatePromise) {
        licenseUpdatePromiseRef.current = null;
      }
    }
  };

  const clearGeolocation = async () => {
    const res = await postApi('/restapi/v1/user/clear_geocomply', {});
    console.log('- geolocation cleared');
  };

  const getIp = async () => {
    const res = await fetch(`https://api.ipify.org?format=json`);
    const resJson = await res.json();
    // @ts-ignore
    localStorage.setItem('user-' + user?.id, resJson.ip);
  };

  const retryReconnect = () => {
    const client = window.GeoComply?.Client;
    if (!client) {
      console.log('- reconnect error no Geocomply.Client');
      return;
    }
    setIsLnaHint(false);
    setLnaHint({ description: '', image: '', title: '' });
    setValidating(true);
    setInProgress(true);
    hasInitRefApi.current = false;
    // onConnect bails on this guard, so without resetting it a reconnect never
    // requests a geolocation and never re-arms the periodic timer (DEV-2860).
    hasInitRef.current = false;
    setErrorMessages([t('geo_validation_in_progress')]);
    client.stopMyIpService(true);
    client.disconnect();
    setTimeout(() => {
      client.connect(installerKey, environment);
    }, 2000);

    console.log('reconnect success');
  };

  const scheduleGeoRevalidation = useCallback(
    (ms: number) => {
      if (geoTimeoutRef.current) {
        clearTimeout(geoTimeoutRef.current);
      }

      // A missing/zero/negative interval used to mean "schedule nothing", which
      // silently disabled every automatic check for the rest of the session.
      const delayMs =
        Number.isFinite(ms) && ms > 0 ? ms : GEO_REVALIDATION_FALLBACK_MS;

      // Deliberately not async: this tick must never await anything. Awaiting a
      // license refresh here meant a rejected request skipped requestGeolocation
      // entirely and, with nothing re-arming the timer, killed every later
      // automatic check for the session (DEV-2860).
      geoTimeoutRef.current = setTimeout(() => {
        console.log('- geolocation validate : in timeout');
        geoTimeoutRef.current = null;
        Lockr.rm('geoValid');
        setValidating(true);
        hasInitRefApi.current = false;
        // Replace rather than append: this can re-run on the retry watchdog below.
        setErrorMessages([t('geo_comply_in_progress')]);
        setBanner(true);

        // Read the client once: an unguarded property access on a missing SDK
        // would throw here and take the chain down with it.
        const client = window.GeoComply?.Client;
        client?.setReasonCode(client?.ReasonCode?.INTERVAL);
        client?.setGeolocationReason('Periodic');
        if (user?.id && client) {
          // Re-assert the user id before every geolocation request so a
          // reconnect (which clears the client's stored id) never leaves the
          // geolocation packet with an empty username (DEV-1772).
          client.setUserId(user.id);
          client.requestGeolocation();
        } else {
          console.log('- geolocation request skipped: no user id or client');
        }

        // Re-arm a short watchdog. A successful check answers on the
        // `geolocation` event and reschedules with the real interval, replacing
        // this one; if this attempt produced nothing the chain still recovers.
        scheduleGeoRevalidationRef.current?.(GEO_REVALIDATION_RETRY_MS);
      }, delayMs);
    },
    [user?.id, t],
  );

  // Lets the timeout above re-arm itself without depending on its own identity.
  useEffect(() => {
    scheduleGeoRevalidationRef.current = scheduleGeoRevalidation;
  }, [scheduleGeoRevalidation]);

  const initGeoComply = useCallback(() => {
    const client = window.GeoComply?.Client;
    if (!client) return;
    let hinted = isLnaHint;

    // --- define handlers as stable refs (so off works) ---
    const onConnect = async () => {
      if (hasInitRef.current) return;

      setTimeout(async () => {
        if (hinted) {
          return;
        }

        console.log(`- connected     : v${client.getVersion()}`);
        setIsConnected(true);
        const lockKey = Lockr.get('geoValid');

        const validUntil = lockKey ? Number(lockKey) : 0;
        const remainingMs = validUntil - Date.now();

        !!lockKey &&
          console.log('- geolocation valid until : ', [
            lockKey,
            validUntil,
            remainingMs,
          ]);

        if (!!lockKey && remainingMs > 0) {
          console.log('- geolocation still valid, skipping request');
          setIsGeoValid(true);
          setValidating(false);
          setBanner(false);
          setShowModal(false);
          scheduleGeoRevalidation(remainingMs);
        } else {
          // @ts-ignore
          Lockr.rm('geoValid');
          // Initial load only: fetches the license and starts its own refresh
          // schedule. Later geolocation requests reuse whatever it holds.
          await updateLicense(true);
          client.startMyIpService(true);
          if (user?.id) {
            client.setUserId(user.id);
            client.requestGeolocation();
          } else {
            // Never request a geolocation without a user id, otherwise the
            // device gets tied to an empty username in GeoComply (DEV-1772).
            console.log('- geolocation request skipped: missing user id');
          }
        }

        hasInitRef.current = true;
      }, 2100);
    };

    const onError = (errorCode: number, errorMessage: string) => {
      showMessage(`Error ${errorCode}: ${errorMessage}`);
      console.log('- error message : ', errorMessage);
      console.log('- error code    : ', errorCode);
      setErrorCode(errorCode);
      setErrorMessages([errorMessage]);
      setIsGeoValid(false);
      setShowModal(true);
      setInProgress(false);
      setValidating(false);
      // ...
    };

    const onGeolocation = async (encrypted: string) => {
      console.log('- geolocation received : ', encrypted.substring(0, 10));
      try {
        const res = await postApi<RailsApiResponse<GeoComplyValidate>>(
          '/restapi/v1/geocomply/validate',
          {
            text: encrypted,
          },
        );

        setValidating(false);
        setInProgress(false);
        const data = res.Data;
        setIsGeoValid(data?.Success);
        console.log('- geolocation validate : Success ', data?.Success);
        console.log('- geolocation validate : Message ', data?.Messages?.[0]);
        console.log('- geolocation validate : Code ', data?.Code);

        if (data?.Success === false) {
          showMessage(data?.Messages?.[0] ?? 'Geolocation failed');
          setErrorCode(data?.Code);
          setBanner(true);
          setShowModal(true);
          setErrorMessages([data?.Messages?.[0] ?? 'Geolocation failed']);
          Lockr.rm('geoValid');
          return;
        }
        if (data?.Success && data.GeolocateIn) {
          const revalidateTimeInMs = parseInt(data.GeolocateIn) * 1000;
          setShowModal(false);
          setBanner(false);
          setErrorCode(null);
          setErrorMessages([]);
          setError(null);
          if (!isNaN(revalidateTimeInMs)) {
            const validUntil = Date.now() + revalidateTimeInMs;
            Lockr.set('geoValid', validUntil.toString());
            scheduleGeoRevalidation(revalidateTimeInMs);
          }
          return;
        }
      } catch (e) {
        showError(e instanceof Error ? e.message : 'Request failed');
      }
    };

    const onMyIpSuccess = async (ip: string, code: any) => {
      const current = localStorage.getItem('user-' + user?.id);
      console.log('- ip change : new ', ip);
      console.log('- ip change : old ', current);
      console.log('- ip change : code ', code);
      client.ackMyIpSuccess();
      if (current !== ip) {
        // @ts-ignore
        Lockr.rm('geoValid');
        client.setReasonCode(client.ReasonCode.IP_CHANGE);
        client.setGeolocationReason('IP changed');
        setValidating(true);
        setInProgress(true);
        setShowModal(true);
        showError('Ip changed! Checking your location. Please wait...');
        localStorage.setItem('user-' + user?.id, ip);
        hasInitRefApi.current = false;
        hasInitRef.current = false;
        await retryGeolocation(true);
        console.log('- ip change : retry trigger success');
        return;
      }
    };

    const onMyIpFailure = (code: number, message: string) => {
      showMessage(`IP service error ${code}: ${message}`);
    };

    const onHint = (errorCode: string, errorMessage: any) => {
      showMessage(`Error ${errorCode}: ${errorMessage}`);
      console.log('- error message : ', errorMessage);
      console.log('- error code    : ', errorCode);
      // ...

      if (errorCode === 'lna_prompt' || errorCode === 'lna_denied') {
        hinted = true;
        setIsLnaHint(true);
        setErrorMessages([]);
        setLnaHint({
          description: errorMessage.messages[0].description,
          image: errorMessage.messages[0].image,
          title: errorMessage.description,
        });
        setErrorCode(612);
        setValidating(false);
        setErrorMessages([errorMessage.description]);
        setShowModal(true);
      }
    };

    // --- attach ---
    client.on('connect', onConnect);
    client.on('error', onError);
    client.on('geolocation', onGeolocation);
    client.on('my_ip_success', onMyIpSuccess);
    client.on('my_ip_failure', onMyIpFailure);
    client.on('hint', onHint);

    client.connect(installerKey, environment);

    return () => {
      // these method names vary by SDK; use what GeoComply provides:
      client.off?.('connect', onConnect);
      client.off?.('error', onError);
      client.off?.('geolocation', onGeolocation);
      client.off?.('my_ip_success', onMyIpSuccess);
      client.off?.('my_ip_failure', onMyIpFailure);
      client.off?.('hint', onHint);

      // sometimes it’s removeListener instead of off
      client.removeListener?.('connect', onConnect);
      client.removeListener?.('error', onError);
      client.removeListener?.('geolocation', onGeolocation);
      client.removeListener?.('my_ip_success', onMyIpSuccess);
      client.removeListener?.('my_ip_failure', onMyIpFailure);
      client.removeListener?.('hint', onHint);
    };
  }, [isLnaHint, t, user?.id]);

  useEffect(() => {
    const scriptId = 'geocomply-sdk';
    let unsubscribe: undefined | (() => void);
    let cancelled = false;

    if (isGeoComplyDisabled) {
      hasInitializedRef.current = false;
      hasInitRef.current = false;
      hasInitRefApi.current = false;
      setIsSdkLoaded(false);
      setIsConnected(false);
      resetGeoState();
      window.GeoComply?.Client?.stopMyIpService();
      window.GeoComply?.Client?.disconnect();
      return;
    }
    if (!user.logged_in || !user?.id) return;
    if (typeof window === 'undefined') return;
    if (hasInitializedRef.current) return;

    const insertGeoComplyScript = async (): Promise<boolean> =>
      new Promise((resolve, reject) => {
        if (document.getElementById(scriptId)) return resolve(true);
        const scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'module');
        scriptElement.id = scriptId;
        scriptElement.setAttribute(
          'src',
          `https://cdn.geocomply.com/plc/4.3.0.0/geocomply-client.min.js`,
        );
        scriptElement.addEventListener('load', () => resolve(true));
        scriptElement.addEventListener('error', err => reject(err));
        document.head.appendChild(scriptElement);
        getIp();
        setErrorMessages([]);
      });

    insertGeoComplyScript().then(ready => {
      if (cancelled) return;
      if (ready) {
        console.log('- SDK loaded');
        if (!window.GeoComply?.Client) {
          console.log('- GeoComply.Client not found on window');
          return;
        }
        if (
          window.location.href ===
          `${window.location.origin}/account/deposit-response`
        ) {
          return;
        }
        setIsSdkLoaded(true);
        unsubscribe = initGeoComply();
        hasInitializedRef.current = true;
      }
    });

    const cleanup = () => {
      try {
        unsubscribe?.();
        if (licenseTimeoutRef.current) {
          clearTimeout(licenseTimeoutRef.current);
          licenseTimeoutRef.current = null;
        }
        if (geoTimeoutRef.current) {
          clearTimeout(geoTimeoutRef.current);
          geoTimeoutRef.current = null;
        }
        window.GeoComply?.Client?.stopMyIpService();
        window.GeoComply?.Client?.disconnect();
        document.head.removeChild(document.getElementById(scriptId)!);
      } catch {
        // ignore
      }
    };
    return () => {
      cancelled = true;
      cleanup();
    };
  }, [isGeoComplyDisabled, resetGeoState, user?.id]); // 👈 depend on auth

  const retryGeolocation = async (ipChange?: boolean) => {
    console.log('- geolocation retry');
    const shouldRefreshLicense =
      errorCode === GeoComplyErrorCodes.CLNT_ERROR_GEOLOCATION_INVALID_LICENSE;
    setValidating(true);
    setInProgress(true);
    setShowModal(true);
    hasInitRefApi.current = false;
    setErrorCode(null);
    setErrorMessages([t('geo_validation_in_progress')]);
    if (!ipChange) {
      setRetryCount(c => (c ?? 0) + 1);
    }
    Lockr.rm('geoValid');
    if (user?.id) {
      // Re-assert the user id before retrying so a prior reconnect never
      // leaves the geolocation packet with an empty username (DEV-1772).
      // Most retries reuse the active license so they cannot be blocked by a
      // license request (DEV-2860). Error 698 specifically means the PLC
      // rejected that active license, though, so retrying it unchanged can
      // never recover.
      if (shouldRefreshLicense) {
        try {
          await updateLicense();
        } catch (e) {
          setValidating(false);
          setInProgress(false);
          showError(e instanceof Error ? e.message : 'License update failed');
          return;
        }
      }
      window.GeoComply?.Client.setUserId(user.id);
      window.GeoComply?.Client.requestGeolocation();
    } else {
      console.log('- geolocation request skipped: missing user id');
    }
  };

  const onCancelModal = async () => {
    hasInitializedRef.current = false;
    hasInitRef.current = false;
    hasInitRefApi.current = false;
    // Clear the failure that opened this modal up front, so it cannot survive
    // into the next player's session on this tab (DEV-2423).
    resetGeoState();
    window.GeoComply?.Client?.stopMyIpService();
    window.GeoComply?.Client?.disconnect();
    await clearGeolocation();
    await signout();
    console.log('- geolocation cancelled and logout');
  };

  useEffect(() => {
    // Reset on every change of player, not just on logout. Relying on the
    // logged-out pass alone left the new player looking at the previous
    // player's result whenever the two logins were not separated by a render
    // with no user id at all (DEV-2423). This only touches display state, so
    // the fresh check kicked off by the SDK effect above is unaffected — its
    // result arrives asynchronously, well after this runs.
    resetGeoState();

    if (!user?.id) {
      hasInitializedRef.current = false;
      hasInitRef.current = false;
      hasInitRefApi.current = false;
      if (licenseTimeoutRef.current) {
        clearTimeout(licenseTimeoutRef.current);
        licenseTimeoutRef.current = null;
      }
      if (geoTimeoutRef.current) {
        clearTimeout(geoTimeoutRef.current);
        geoTimeoutRef.current = null;
      }
      // @ts-ignore

      window.GeoComply?.Client?.stopMyIpService();
      window.GeoComply?.Client?.disconnect();
    }
  }, [user?.id, resetGeoState]);

  return (
    <GeoComplyContext.Provider
      value={{
        isSdkLoaded,
        isConnected,
        errorMessages,
        banner,
        error,
        retryCount,
        retryGeolocation,
        retryReconnect,
        onCancelModal,
        errorCode,
        showModal,
        setShowModal,
        validating,
        inProgress,
        isGeoValid,
        lnaHint,
        isLnaHint,
      }}
    >
      {children}
    </GeoComplyContext.Provider>
  );
};

export const useGeoComplyMk2 = () => {
  const ctx = useContext(GeoComplyContext);
  if (!ctx) {
    throw new Error('useGeoComply must be used within a GeoComplyProvider');
  }
  return ctx;
};
